<ion-header>
  <ion-toolbar color="light">
    <ion-title>Select an Image</ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="dismissModal()">
        <ion-icon name="close"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>

  <ion-toolbar color="light">
    <ion-segment [(ngModel)]="selectedTab" class="ion-justify-content-center full-width">
      <ion-segment-button value="add">
        <ion-label>Add</ion-label>
      </ion-segment-button>
      <ion-segment-button value="list" [disabled]="images.length === 0">
        <ion-label>
          List
          <span *ngIf="images?.length">({{ images.length }})</span>
        </ion-label>
      </ion-segment-button>
      <ion-segment-button value="screenshot">
        <ion-label>Capture</ion-label>
      </ion-segment-button>
      <ion-segment-button value="edit" [disabled]="images.length === 0">
        <ion-label>Edit</ion-label>
      </ion-segment-button>
    </ion-segment>
  </ion-toolbar>
</ion-header>

<ion-content>
  <ng-container [ngSwitch]="selectedTab">
    <!-- ADD (Merged Upload + Search) -->
    <ng-template [ngSwitchCase]="'add'">
      <ion-list lines="none">
        <ion-item lines="full" class="add-bar">
          <ion-textarea placeholder="Search images..." auto-grow="true" [(ngModel)]="searchQuery"
              (keyup.enter)="onEnter()" [disabled]="isSearching">
          </ion-textarea>
          <ion-buttons slot="end">
              <ion-button color="success" *ngIf="inputIsImageUrl" (click)="acceptSearchUrl()" [disabled]="!searchQuery || isVerifyingSelection">
                <ion-spinner *ngIf="isVerifyingSelection" name="crescent"></ion-spinner>
                <ion-icon *ngIf="!isVerifyingSelection" name="checkmark-outline"></ion-icon>
              </ion-button>
              <ion-button color="primary" (click)="searchImages()" [disabled]="isSearching || !searchQuery || isVerifyingSelection" *ngIf="!inputIsImageUrl">
                <ion-spinner *ngIf="isSearching" name="crescent"></ion-spinner>
                <ion-icon *ngIf="!isSearching" name="search-outline"></ion-icon>
              </ion-button>
              <ion-button fill="outline" (click)="presentFilterPopover($event)"
                [color]="selectedFilter !== 'All' ? 'warning' : 'medium'" *ngIf="!inputIsImageUrl" [disabled]="isVerifyingSelection">
                <ion-icon name="filter-outline"></ion-icon>
              </ion-button>
            <ion-button fill="clear" (click)="triggerFileInput()" aria-label="Upload" [disabled]="isVerifyingSelection">
              <ion-icon name="cloud-upload-outline"></ion-icon>
            </ion-button>
          </ion-buttons>
          <input #fileInput type="file" accept="image/*" hidden (change)="uploadImage($event)">
        </ion-item>

        <ion-item lines="none" *ngIf="selectionErrorMessage">
          <ion-text color="danger">
            {{ selectionErrorMessage }}
          </ion-text>
        </ion-item>

        <!-- Filter Popover -->
        <ion-popover [isOpen]="showFilterPopover" (didDismiss)="dismissFilterPopover($event)">
          <ng-template>
            <ion-list>
              <ion-radio-group [(ngModel)]="selectedFilter">
                <ion-item lines="none" *ngFor="let f of timeFilters">
                  <ion-radio slot="start" labelPlacement="end" [value]="f">{{ f }}</ion-radio>
                </ion-item>
              </ion-radio-group>
            </ion-list>
            <ion-button expand="full" (click)="applyFilter()">Apply</ion-button>
          </ng-template>
        </ion-popover>

        <ion-list *ngIf="searchResults?.length">
          <ion-item lines="full">
            <ion-label>Search Results</ion-label>
            <ion-badge color="primary">{{ searchResults.length }}</ion-badge>
          </ion-item>
          <ion-grid>
            <ion-row>
              <ion-col size="6" size-md="4" size-lg="3" *ngFor="let image of searchResults">
                <ion-card (click)="selectImage(image.link)" [class.card-disabled]="isVerifyingSelection">
                  <div class="image-container">
                    <img [src]="image.link" (load)="onImageLoaded(image.link, $event)" loading="lazy" />
                    <div class="image-size-label" *ngIf="imageSizes[image.link]">
                      {{ imageSizes[image.link].width }}x{{ imageSizes[image.link].height }}
                    </div>
                    <div *ngIf="imageSizes[image.link] && (imageSizes[image.link].width < 500 || imageSizes[image.link].height < 500)"
                         class="image-warning-overlay">
                      <ion-icon name="warning"></ion-icon>
                    </div>
                  </div>
                </ion-card>
              </ion-col>
            </ion-row>
          </ion-grid>
        </ion-list>
      </ion-list>
    </ng-template>

    <!-- LIST -->
    <ng-template [ngSwitchCase]="'list'">
      <!-- Select from List Tab Content -->
      <ion-grid>
        <ion-row>
          <ion-col size="6" size-md="4" size-lg="3" *ngFor="let imageUrl of images">
            <ion-card (click)="selectImage(imageUrl)" [class.card-disabled]="isVerifyingSelection">
              <div class="image-container">
                <img [src]="imageUrl" (load)="onImageLoaded(imageUrl, $event)" />
                <div class="image-size-label" *ngIf="imageSizes[imageUrl]">
                  {{ imageSizes[imageUrl].width }}x{{ imageSizes[imageUrl].height }}
                </div>
                <div *ngIf="imageSizes[imageUrl] && (imageSizes[imageUrl].width < 500 || imageSizes[imageUrl].height < 500)"
                     class="image-warning-overlay">
                  <ion-icon name="warning"></ion-icon>
                </div>
              </div>
            </ion-card>
          </ion-col>
          <ion-col size="6" size-md="4" size-lg="3">
            <ion-card (click)="clearImage()" class="no-image-card" [class.card-disabled]="isVerifyingSelection">
              <div class="no-image-placeholder">
                <ion-icon name="close-circle-outline" class="no-image-icon"></ion-icon>
              </div>
            </ion-card>
          </ion-col>
        </ion-row>
      </ion-grid>
    </ng-template>
    <!-- SCREENSHOT -->
    <ng-template [ngSwitchCase]="'screenshot'">
      <!-- Get Screenshot Tab Content -->
      <ion-item>
        <ion-textarea label="URL:" label-placement="floating" [(ngModel)]="url" [disabled]="isCapturing"
          auto-grow="true"></ion-textarea>
        <ion-button (click)="captureScreenshot()">
          <ion-spinner *ngIf="isCapturing" name="crescent"></ion-spinner>
          <ion-icon *ngIf="!isCapturing" name="camera-outline"></ion-icon>
        </ion-button>
      </ion-item>
      <ion-item>
        <ion-text color="medium">
          <p>
            <strong>Beta Feature:</strong> This screenshot functionality is in beta and may not work reliably with every
            website.
            If a screenshot fails, please use your system's PrintScreen function, save the image to disk, and use the
            Upload tab.
          </p>
          <p>If there are minor tweaks needed to the capture:
            <span>
              <a (click)="createSupportTicket()"
                style="color: var(--ion-color-medium); text-decoration: underline; cursor: pointer;">Click here to
                create a support ticket.</a>
            </span>
          </p>
        </ion-text>
      </ion-item>
    </ng-template>

    <!-- EDIT -->
    <ng-template [ngSwitchCase]="'edit'">
      <app-edit-social-card [post]="post"></app-edit-social-card>
      <ion-button (click)="dismissModal()" expand="full">
        Close
      </ion-button>
    </ng-template>
  </ng-container>
</ion-content>